import type { CyanGoal, CyanGoalStep, GoalAuthority, GoalRisk } from "./types";

export interface DecompositionStage {
  key: string;
  title: string;
  description: string;
  authority: GoalAuthority;
  risk: GoalRisk;
  reversible: boolean;
  capabilities: string[];
}

const BASE_STAGES: readonly DecompositionStage[] = [
  {
    key: "ground",
    title: "Ground current state",
    description:
      "Collect authoritative project, SPEC, runtime, connection and history evidence.",
    authority: "observe",
    risk: "low",
    reversible: true,
    capabilities: [
      "project-intake",
      "spec-read",
      "memory-read",
      "connection-discovery",
    ],
  },

  {
    key: "diagnose",
    title: "Diagnose gaps",
    description:
      "Compare current state against owner goal and canonical requirements.",
    authority: "plan",
    risk: "low",
    reversible: true,
    capabilities: ["analysis", "gap-detection"],
  },

  {
    key: "prepare",
    title: "Prepare implementation",
    description: "Prepare exact governed mutations and verification strategy.",
    authority: "prepare",
    risk: "medium",
    reversible: true,
    capabilities: ["mutation-planning", "verification-planning"],
  },

  {
    key: "execute",
    title: "Execute approved work",
    description:
      "Execute the approved implementation through governed workers.",
    authority: "execute_reversible",
    risk: "medium",
    reversible: true,
    capabilities: ["execution-queue", "technology-worker"],
  },

  {
    key: "verify",
    title: "Verify result",
    description: "Verify actual outcome using authoritative evidence.",
    authority: "observe",
    risk: "low",
    reversible: true,
    capabilities: [
      "tests",
      "build",
      "browser-verification",
      "runtime-observation",
    ],
  },
];

export class CyanPlanDecompositionEngine {
  decompose(goal: CyanGoal): CyanGoalStep[] {
    return BASE_STAGES.map((stage, index) => {
      const previous = index > 0 ? BASE_STAGES[index - 1] : undefined;

      return {
        id: `${goal.id}:${stage.key}`,

        goalId: goal.id,

        title: stage.title,

        description: stage.description,

        authority: stage.authority,

        risk: stage.risk,

        reversible: stage.reversible,

        dependencies: previous ? [`${goal.id}:${previous.key}`] : [],

        capabilities: [...stage.capabilities],

        status: index === 0 ? "ready" : "pending",
      };
    });
  }
}
